import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import type { PlanungAssignmentLinkCompare, PlanungRow } from "@/lib/montageplanung";
import { formatDateDe } from "./dateUtils";
import { rowLabelFull } from "./displayLabels";

function formatRange(from: string, to: string): string {
  const f = from.slice(0, 10);
  const t = to.slice(0, 10);
  return f === t ? formatDateDe(f) : `${formatDateDe(f)} – ${formatDateDe(t)}`;
}

export function AssignmentLinkResolveDialog({
  open,
  onClose,
  compare,
  rows,
  onConfirm,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  compare: PlanungAssignmentLinkCompare | null;
  rows: PlanungRow[];
  onConfirm: (contentSourceAssignmentId: number) => void;
  loading?: boolean;
  error?: string | null;
}) {
  const [sourceId, setSourceId] = useState<number | null>(null);

  const rowById = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of rows) {
      map.set(row.id, rowLabelFull(row));
    }
    return map;
  }, [rows]);

  const assignments = compare?.assignments ?? [];

  useEffect(() => {
    if (!open) {
      setSourceId(null);
      return;
    }
    if (assignments.length > 0) {
      setSourceId(assignments[0]!.id);
    }
  }, [open, compare]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Inhalt der Verknüpfung wählen"
      description="Die Einsätze unterscheiden sich. Welchen Inhalt (inkl. Feinplanung) sollen alle verknüpften Einsätze übernehmen?"
      size="lg"
      zIndexClass="z-[230]"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            disabled={loading || sourceId == null}
            onClick={() => sourceId != null && onConfirm(sourceId)}
          >
            Verknüpfen und übernehmen
          </Button>
        </>
      }
      bodyClassName="max-h-[min(70vh,560px)] overflow-y-auto"
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="space-y-2">
        {assignments.map((item) => (
          <label
            key={item.id}
            className="flex cursor-pointer items-start gap-3 rounded-ios border border-separator bg-fill-tertiary/30 px-3 py-2 has-[:checked]:border-accent has-[:checked]:bg-accent/5"
          >
            <input
              type="radio"
              name="mp-link-content-source"
              className="mt-1"
              checked={sourceId === item.id}
              onChange={() => setSourceId(item.id)}
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-label">{item.title}</span>
              <span className="block text-xs text-label-secondary">
                {rowById.get(item.row_id) ?? `Zeile #${item.row_id}`} ·{" "}
                {formatRange(item.date_from, item.date_to)}
              </span>
            </span>
          </label>
        ))}
      </div>

      {compare && compare.differing_fields.length > 0 && (
        <div className="mt-4 space-y-3 border-t border-separator pt-4">
          <h3 className="text-sm font-medium text-label">Unterschiede</h3>
          {compare.differing_fields.map((field) => (
            <div key={field.field} className="rounded-ios border border-separator overflow-hidden">
              <div className="bg-fill-tertiary/50 px-3 py-1.5 text-xs font-semibold text-label-secondary">
                {field.label}
              </div>
              <div className="divide-y divide-separator">
                {assignments.map((item) => (
                  <div key={item.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2 px-3 py-2 text-sm">
                    <div className="truncate text-label-secondary">
                      {item.title}
                    </div>
                    <div className="whitespace-pre-wrap text-label">
                      {field.values[String(item.id)] ?? field.values[item.id] ?? "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}
